import {NgFor, NgIf} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Optional} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatRadioModule} from '@angular/material/radio';
import {MatSelectModule} from '@angular/material/select';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {LocalStorageService} from 'ngx-webstorage';
import type {VatExportPropertyLabel, VatObjektExportFormat} from '../../export/vat-export.types';
import {VAT_OBJEKT_DIALOG_COPY} from '../../vat-copy';

/** Zeitraum-Option: `key` null = Kalenderjahr, sonst 'M01'…'M12' | 'Q1'…'Q4'. */
export interface VatObjektPeriodOption {
    key: string | null;
    label: string;
}

export interface VatObjektDialogData {
    properties: VatExportPropertyLabel[];
    /** Alle Zeiträume des Jahres laut Profil (`periodKeysFor`) plus Kalenderjahr (`key: null`). */
    periodOptions: VatObjektPeriodOption[];
    preselect: {
        propertyID: string | null;
        /** Vorbelegter Voranmeldungszeitraum (Karte/Export-Sheet) – null = Kalenderjahr vorbelegt. */
        periodKey: string | null;
        format?: VatObjektExportFormat;
        saveToFolder?: boolean;
    };
}

export interface VatObjektDialogResult {
    propertyID: string;
    periodKey: string | null;
    format: VatObjektExportFormat;
    saveToFolder: boolean;
}

export type VatObjektPeriodChoice = 'CURRENT' | 'YEAR' | 'OTHER';

/** Gleicher Schlüssel wie `ExportBottomSheetComponent.saveToFolder` – ein Toggle-Zustand für Sheet und Dialog. */
export const VAT_OBJEKT_SAVE_TO_FOLDER_KEY = 'ExportBottomSheetComponent.saveToFolder';

/**
 * Vor-Dialog der „Umsatzsteuerabrechnung je Objekt“ (Spec 2.4, Muster vat-stationery-dialog): Immobilie (Select),
 * Zeitraum (Radio: vorbelegter Voranmeldungszeitraum | Kalenderjahr | anderer Zeitraum), Format (PDF/Excel) und
 * „Im Dateimanager speichern“ (LocalStorage wie das Export-Sheet). Rechnet nichts – die Fassade übernimmt.
 * Overlay → eigener ChangeDetectorRef (OnPush).
 *
 * @author Anthony Nahas
 * @since 13.09.26
 */
@Component({
    selector: 'vat-objekt-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, NgFor, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatSelectModule, MatRadioModule, MatSlideToggleModule],
    template: `
        <h2 mat-dialog-title class="text-lg font-semibold">{{ copy.title }}</h2>
        <mat-dialog-content class="flex flex-col gap-5">
            <p class="text-secondary text-md">{{ copy.intro }}</p>

            <!-- Immobilie -->
            <div *ngIf="!data.properties.length" class="rounded-xl border p-4 text-secondary" data-testid="vat-objekt-no-properties">{{ copy.property.none }}</div>
            <mat-form-field *ngIf="data.properties.length" appearance="fill" class="w-full" subscriptSizing="dynamic">
                <mat-label>{{ copy.property.label }}</mat-label>
                <mat-select [formControl]="propertyControl" [placeholder]="copy.property.placeholder" data-testid="vat-objekt-property">
                    <mat-option *ngFor="let property of data.properties" [value]="property.id">{{ property.label }}</mat-option>
                </mat-select>
            </mat-form-field>

            <!-- Zeitraum -->
            <fieldset class="flex flex-col gap-2 border-0 p-0 m-0 min-w-0">
                <legend class="text-sm font-semibold text-secondary mb-1">{{ copy.period.legend }}</legend>
                <mat-radio-group [formControl]="periodChoiceControl" class="flex flex-col gap-2" data-testid="vat-objekt-period-choice">
                    <mat-radio-button *ngIf="currentOption" value="CURRENT" color="primary">{{ copy.period.current(currentOption.label) }}</mat-radio-button>
                    <mat-radio-button value="YEAR" color="primary">{{ yearLabel }}</mat-radio-button>
                    <mat-radio-button *ngIf="otherOptions.length" value="OTHER" color="primary">{{ copy.period.other }}</mat-radio-button>
                </mat-radio-group>
                <mat-form-field *ngIf="periodChoiceControl.value === 'OTHER'" appearance="fill" class="w-full sm:max-w-xs" subscriptSizing="dynamic">
                    <mat-label>{{ copy.period.otherSelect }}</mat-label>
                    <mat-select [formControl]="otherPeriodControl" data-testid="vat-objekt-other-period">
                        <mat-option *ngFor="let option of otherOptions" [value]="option.key">{{ option.label }}</mat-option>
                    </mat-select>
                </mat-form-field>
            </fieldset>

            <!-- Format -->
            <fieldset class="flex flex-col gap-2 border-0 p-0 m-0 min-w-0">
                <legend class="text-sm font-semibold text-secondary mb-1">{{ copy.format.legend }}</legend>
                <mat-radio-group [formControl]="formatControl" class="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="vat-objekt-format">
                    <!-- Karte als Ganzes klickbar (Review C12): Klick auf Padding/Rand wählt das Format, nicht nur der Radio-Kreis -->
                    <div class="rounded-xl border p-3 cursor-pointer flex items-start gap-2 hover:border-primary transition-colors" data-testid="vat-objekt-format-pdf"
                           [class.border-primary]="formatControl.value === 'pdf'" (click)="selectFormat('pdf', $event)">
                        <mat-radio-button value="pdf" color="primary">
                            <span class="font-semibold">{{ copy.format.pdf }}</span>
                            <span class="block text-xs text-secondary">{{ copy.format.pdfHint }}</span>
                        </mat-radio-button>
                    </div>
                    <div class="rounded-xl border p-3 cursor-pointer flex items-start gap-2 hover:border-primary transition-colors" data-testid="vat-objekt-format-excel"
                           [class.border-primary]="formatControl.value === 'excel'" (click)="selectFormat('excel', $event)">
                        <mat-radio-button value="excel" color="primary">
                            <span class="font-semibold">{{ copy.format.excel }}</span>
                            <span class="block text-xs text-secondary">{{ copy.format.excelHint }}</span>
                        </mat-radio-button>
                    </div>
                </mat-radio-group>
            </fieldset>

            <!-- Ablage -->
            <div class="flex flex-col gap-1">
                <mat-slide-toggle [formControl]="saveToFolderControl" color="primary" data-testid="vat-objekt-save-to-folder">{{ copy.saveToFolder }}</mat-slide-toggle>
                <span class="text-xs text-secondary">{{ copy.saveToFolderHint }}</span>
            </div>
        </mat-dialog-content>
        <mat-dialog-actions align="end">
            <button mat-stroked-button type="button" (click)="dialogRef.close(null)">{{ copy.cancel }}</button>
            <button mat-flat-button color="primary" type="button" data-testid="vat-objekt-confirm" [disabled]="!canConfirm" (click)="confirm()">
                <mat-icon class="icon-size-5" [svgIcon]="formatControl.value === 'excel' ? 'heroicons_outline:table' : 'heroicons_outline:document-text'"></mat-icon>
                <span class="ml-2">{{ copy.confirm }}</span>
            </button>
        </mat-dialog-actions>
    `
})
export class VatObjektDialogComponent {
    readonly copy = VAT_OBJEKT_DIALOG_COPY;

    readonly currentOption: VatObjektPeriodOption | null;
    readonly yearLabel: string;
    readonly otherOptions: VatObjektPeriodOption[];

    readonly propertyControl: FormControl<string | null>;
    readonly periodChoiceControl: FormControl<VatObjektPeriodChoice>;
    readonly otherPeriodControl: FormControl<string | null>;
    readonly formatControl: FormControl<VatObjektExportFormat>;
    readonly saveToFolderControl: FormControl<boolean>;

    constructor(
        public dialogRef: MatDialogRef<VatObjektDialogComponent, VatObjektDialogResult | null>,
        @Inject(MAT_DIALOG_DATA) public data: VatObjektDialogData,
        private changeDetectorRef: ChangeDetectorRef,
        @Optional() private localStorage: LocalStorageService | null,
    ) {
        const options = data.periodOptions ?? [];
        const preselectKey = data.preselect?.periodKey ?? null;
        this.currentOption = preselectKey ? options.find(option => option.key === preselectKey) ?? null : null;
        const yearOption = options.find(option => option.key === null);
        this.yearLabel = yearOption?.label ?? '';
        this.otherOptions = options.filter(option => option.key !== null && option.key !== preselectKey);

        const properties = data.properties ?? [];
        const preselectProperty = properties.find(property => property.id === data.preselect?.propertyID)?.id
            ?? (properties.length === 1 ? properties[0].id : null);
        this.propertyControl = new FormControl<string | null>(preselectProperty);
        this.periodChoiceControl = new FormControl<VatObjektPeriodChoice>(this.currentOption ? 'CURRENT' : 'YEAR', {nonNullable: true});
        this.otherPeriodControl = new FormControl<string | null>(this.otherOptions[0]?.key ?? null);
        this.formatControl = new FormControl<VatObjektExportFormat>(data.preselect?.format ?? 'pdf', {nonNullable: true});
        this.saveToFolderControl = new FormControl<boolean>(data.preselect?.saveToFolder ?? this.storedSaveToFolder(), {nonNullable: true});

        this.periodChoiceControl.valueChanges.subscribe(() => this.changeDetectorRef.markForCheck());
        this.formatControl.valueChanges.subscribe(() => this.changeDetectorRef.markForCheck());
        this.propertyControl.valueChanges.subscribe(() => this.changeDetectorRef.markForCheck());
    }

    /**
     * Karten-Klick (Review C12/B5): Klicks auf den Radio-Button selbst (Kreis oder Label) lässt der Handler durch – dort schaltet
     * der ControlValueAccessor genau einmal; nur Klicks auf Rand/Padding setzen das Format, und nur bei Änderung (kein Doppel-Emit).
     */
    selectFormat(format: VatObjektExportFormat, event: Event): void {
        if ((event.target as HTMLElement | null)?.closest?.('mat-radio-button')) {
            return;
        }
        if (this.formatControl.value !== format) {
            this.formatControl.setValue(format);
        }
    }

    get canConfirm(): boolean {
        return !!this.propertyControl.value && this.resolvedPeriodKey() !== undefined;
    }

    confirm(): void {
        const propertyID = this.propertyControl.value;
        const periodKey = this.resolvedPeriodKey();
        if (!propertyID || periodKey === undefined) {
            this.propertyControl.markAsTouched();
            this.changeDetectorRef.markForCheck();
            return;
        }
        const saveToFolder = this.saveToFolderControl.value;
        this.rememberSaveToFolder(saveToFolder);
        this.dialogRef.close({propertyID, periodKey, format: this.formatControl.value, saveToFolder});
    }

    /** null = Kalenderjahr, string = Zeitraumschlüssel, undefined = unvollständig (OTHER ohne Auswahl). */
    private resolvedPeriodKey(): string | null | undefined {
        switch (this.periodChoiceControl.value) {
            case 'CURRENT':
                return this.currentOption?.key ?? null;
            case 'OTHER':
                return this.otherPeriodControl.value ?? undefined;
            default:
                return null;
        }
    }

    private storedSaveToFolder(): boolean {
        try {
            return this.localStorage?.retrieve(VAT_OBJEKT_SAVE_TO_FOLDER_KEY) === true;
        } catch {
            return false;
        }
    }

    private rememberSaveToFolder(value: boolean): void {
        try {
            this.localStorage?.store(VAT_OBJEKT_SAVE_TO_FOLDER_KEY, value);
        } catch {
            // LocalStorage optional (Privatmodus/Tests)
        }
    }
}
